import { Link, Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from 'design-system/react';

import { getSimulation } from '../../client';
import { SimulationContext } from '../../SimulationContext';
import { AppFeatureFlagsProvider } from '../../AppFeatureFlagsProvider';
import { useSimulationEvents } from '../../hooks/useSimulationEvents';
import { Page } from '../organisms';
import { AppShell, type NavItem } from './AppShell';

export function OperatorLayout() {
  const { simId } = useParams<{ simId: string }>();

  if (!simId) {
    return (
      <Page title="Error" hideBackButton>
        <p className="text-feedback-error mt-8 text-center">Invalid URL — missing simulation ID.</p>
        <Link to="/operator" className="text-sm text-action-primary hover:underline mt-4 block text-center">
          &larr; Back to simulations
        </Link>
      </Page>
    );
  }

  return <OperatorLayoutInner simId={simId} />;
}

function OperatorLayoutInner({ simId }: { simId: string }) {
  useSimulationEvents(simId);

  const {
    data: simulation,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['simulation', simId],
    queryFn: () => getSimulation({ path: { sim_id: simId }, throwOnError: true }).then(({ data }) => data),
    retry: 1,
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <Page title="Loading..." hideBackButton>
        <div className="flex flex-col items-center gap-3 mt-8">
          <Spinner size="xl" />
          <p className="text-text-secondary">Loading simulation...</p>
        </div>
      </Page>
    );
  }

  if (isError || !simulation) {
    return (
      <Page title="Not Found" hideBackButton>
        <div className="flex flex-col items-center gap-4 mt-8">
          <p className="text-feedback-error">No simulation found.</p>
          <Link to="/operator" className="text-sm text-action-primary hover:underline">
            &larr; Back to simulations
          </Link>
        </div>
      </Page>
    );
  }

  const basePath = `/operator/${simId}`;
  const items: NavItem[] = [
    { label: 'Operator', to: basePath, end: true },
    { label: 'Dashboard', to: `${basePath}/dashboard` },
    { label: 'Value Chart', to: `${basePath}/value` },
    { label: 'Join QR', to: `${basePath}/qr` },
  ];

  return (
    <SimulationContext.Provider value={{ simId: simulation.id, label: simulation.label }}>
      <AppFeatureFlagsProvider>
        <AppShell items={items} exitTo="/operator">
          <Outlet />
        </AppShell>
      </AppFeatureFlagsProvider>
    </SimulationContext.Provider>
  );
}
